import { readonly, ref } from 'vue'

/**
 * Accès à internet, vérifié en joignant le fournisseur du fond de carte en ligne.
 *
 * navigator.onLine ne suffit pas : il reste à true sur un réseau local sans sortie internet, cas
 * typique d'un déploiement hors ligne où le front, l'API et Martin tournent sur le LAN. On sonde
 * donc réellement l'hôte : une requête no-cors se résout (réponse opaque) dès que l'hôte répond,
 * quel que soit le statut HTTP, et échoue seulement quand il est injoignable. L'URL sondée n'a pas
 * de clé d'API et ne consomme donc pas de quota.
 */
const PROBE_URL =
  (import.meta.env.VITE_CONNECTIVITY_PROBE_URL as string | undefined)?.trim() ||
  'https://api.maptiler.com/maps/streets-v2/style.json'
const PROBE_TIMEOUT_MS = 4000
/** Hors ligne, on revérifie souvent pour rebasculer vite en ligne dès le retour de la connexion. */
const OFFLINE_RECHECK_MS = 15_000
/** En ligne, on revérifie plus rarement : l'événement « offline » du navigateur couvre le cas courant. */
const ONLINE_RECHECK_MS = 60_000

const isOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
let firstCheck: Promise<boolean> | null = null
let recheckTimer: ReturnType<typeof setTimeout> | null = null

async function probe(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  try {
    await fetch(PROBE_URL, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    })
    return true
  } catch {
    return false
  } finally {
    clearTimeout(timeout)
  }
}

async function check(): Promise<boolean> {
  if (recheckTimer != null) clearTimeout(recheckTimer)
  const online = await probe()
  isOnline.value = online
  recheckTimer = setTimeout(check, online ? ONLINE_RECHECK_MS : OFFLINE_RECHECK_MS)
  return online
}

function start(): Promise<boolean> {
  if (firstCheck) return firstCheck
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => void check())
    window.addEventListener('offline', () => {
      isOnline.value = false
      void check()
    })
  }
  firstCheck = check()
  return firstCheck
}

export function useConnectivity() {
  start()
  return {
    /** true tant que le fournisseur du fond de carte en ligne est joignable. */
    isOnline: readonly(isOnline),
    /** Résultat de la première vérification, à attendre avant de choisir le fond initial. */
    ready: () => start(),
  }
}
